import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import {Button, Lead, Loader, Radio, Slot, Text, Trail, Typography} from '@yandex-delivery/blocks'
import {Image} from '@yandex-delivery/blocks/image'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useConfigContext} from '@yandex-delivery/form/utils/contexts/ConfigContext'
import {isEmpty} from '@yandex-delivery/utils'
import clsx from 'clsx'
import {type ReactElement, type ReactNode, memo, useCallback} from 'react'

import type {PaymentCardErrorCode} from '../../../../../../api/payment'
import {EventGoal, useFormUserEventHandler} from '../../../../lib/metrika'
import {getIconSrc} from '../PaymentCard/utils'
import {useButtonProps} from './hooks/useButtonProps'
import * as classes from './PaymentCardButton.classes'

export type PaymentCardButtonProps = {
  cardErrorCode?: PaymentCardErrorCode
  errorText?: string
  isPending: boolean
  onPress?: VoidFunction
  paymentCard?: PaymentCard
  title: ReactNode
}

export const PaymentCardButton = memo(
  ({cardErrorCode, errorText, isPending, onPress, paymentCard}: PaymentCardButtonProps): ReactElement => {
    const props = useButtonProps({cardErrorCode, onPress, paymentCard})
    const i18n = useI18n()
    const config = useConfigContext()
    const unauthorized = isEmpty(config.launch?.passport_account)

    const handleReachGoal = useFormUserEventHandler()

    const handlePress = useCallback(() => {
      handleReachGoal(EventGoal.SelectPaymentCard)
      onPress?.()
    }, [handleReachGoal, onPress])

    return (
      <Slot
        className={clsx(classes.slotButton, {[classes.disabledClicks]: isPending})}
        Component={Button}
        pr="s"
        size="l"
        {...props}
      >
        <Lead>
          {isPending ? (
            <Loader />
          ) : (
            <Image
              alt={paymentCard?.system}
              height={24}
              loading="lazy"
              src={getIconSrc(paymentCard?.system)}
              width={36}
            />
          )}
        </Lead>
        {!isPending && (
          <>
            <Text>
              <Typography group="tight" thickness="regular" variant="body2">
                {paymentCard?.number ? toCardView(paymentCard.number) : i18n('bdui.payment-method.card', 'Карта')}
              </Typography>
              {errorText && (
                <Typography color="error" data-error="true" group="tight" thickness="regular" variant="caption1">
                  {errorText}
                </Typography>
              )}
            </Text>
            <Trail className={classes.trail}>
              {unauthorized ? (
                <a className={classes.link} href={config.getPassportUrl('/auth')}>
                  <Button shape="circle" size="xs" variant="action">
                    {i18n('bdui.payment-method.login', 'Войти в Яндекс ID')}
                  </Button>
                </a>
              ) : (
                <Button
                  onPress={handlePress}
                  shape="circle"
                  size="xs"
                  variant={paymentCard?.number ? 'outline' : 'action'}
                >
                  {paymentCard?.number
                    ? i18n('bdui.payment-method.change', 'Изменить')
                    : i18n('bdui.payment-method.select', 'Выбрать')}
                </Button>
              )}
            </Trail>
            {paymentCard?.number && (
              <Trail ml="s">
                <Radio checked />
              </Trail>
            )}
          </>
        )}
      </Slot>
    )
  },
)

function toCardView(number?: string): string | undefined {
  /** распиливаем номер карты с маской на 4 блока по 4 цифры/звездочки */
  return number?.match(/.{1,4}/g)?.join(' ')
}
